import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useSaldosClientes } from '../../hooks/useSaldosClientes';
import { useActividadClientes } from '../../hooks/useActividadClientes';
import { sumSaldosTotales } from '../../utils/balanceCalculator';
import { formatComitenteForDisplay } from '../../utils/formatters';
import { buildScope, filterByComitentes, filterMapByComitentes } from '../../utils/saldosScope';
import { pendientesDe } from '../../utils/actividadClientes';
import { formatSnapshotHora } from '../../services/saldosService';
import { theme } from '../../config/theme';
import {
    BUCKET_ARS,
    MONEDA_BUCKETS,
    MONEDA_MOVIMIENTO,
    MONEDA_TITULO,
} from '../../constants/monedas';
import IndicatorCard from '../saldos/IndicatorCard';
import DeudoresAlertCard from '../saldos/DeudoresAlertCard';
import SaldoClienteBadge from '../cauciones/SaldoClienteBadge';
import SaldoDesgloseModal from '../SaldoDesgloseModal';
import StackedTable from '../common/StackedTable';

const TabWrapper = styled.div`
  font-family: ${props => props.theme.typography.fontFamily};
`;
const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 1.5rem;
`;
const Title = styled.h2`
  font-family: ${props => props.theme.typography.titleFamily};
  font-size: 2rem;
  color: ${props => props.theme.colors.secondary};
  margin: 0;
`;
const SnapshotBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background-color: rgba(26, 40, 62, 0.08);
  border: 1px solid ${props => props.theme.colors.border};
  color: ${props => props.theme.colors.secondary};
  border-radius: ${props => props.theme.borderRadius};
  padding: 0.5rem 0.85rem;
  font-size: 0.8rem;
  font-weight: 500;
  strong { text-transform: capitalize; color: ${props => props.theme.colors.primary}; }
`;
const WarningBanner = styled.div`
  background-color: #FEF3C7;
  border: 1px solid #FDE68A;
  color: #92400E;
  padding: 0.75rem 1rem;
  border-radius: ${props => props.theme.borderRadius};
  margin-bottom: 1rem;
  font-weight: 600;
  font-size: 0.9rem;
`;
const InfoNote = styled.p`
  margin: 0 0 1rem 0;
  font-size: 0.8rem;
  color: ${props => props.theme.colors.textSecondary};
`;
const IndicatorsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;
const AlertsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(320px, 100%), 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;
const SectionLabel = styled.h3`
  margin: 0 0 0.6rem 0;
  font-family: ${props => props.theme.typography.titleFamily};
  font-size: 0.85rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${props => props.theme.colors.primary};
`;
const SearchInput = styled.input`
  padding: 0.5rem 0.75rem;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius};
  font-family: ${props => props.theme.typography.fontFamily};
  font-size: 0.9rem;
  margin-bottom: 1rem;
  width: 100%;
  max-width: 360px;
  box-sizing: border-box;
`;
const TableContainer = styled.div`
  overflow-x: auto;
  background-color: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius};
  box-shadow: ${props => props.theme.shadows.soft};
`;
// Vista de tarjetas en el celular (ver useStackedTable).
const CLIENTES_STACK = { title: 'Cliente' };

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 720px;
  th {
    background-color: #F9FAFB;
    padding: 0.85rem 1rem;
    text-align: left;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    color: ${props => props.theme.colors.secondary};
    border-bottom: 1px solid ${props => props.theme.colors.border};
    white-space: nowrap;
  }
  td {
    padding: 0.75rem 1rem;
    border-bottom: 1px solid ${props => props.theme.colors.border};
    font-size: 0.875rem;
    vertical-align: middle;
  }
  tbody tr:hover { background-color: #F9FAFB; }
`;
const ClickableCell = styled.td`
  text-align: right;
  cursor: pointer;
`;
const Placeholder = styled.div`
  text-align: center;
  padding: 3rem 2rem;
  background-color: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius};
  color: ${props => props.theme.colors.textSecondary};
  box-shadow: ${props => props.theme.shadows.soft};
  line-height: 1.6;
`;
const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-wrap: wrap;
`;
const ActionButton = styled.button`
  font-family: ${props => props.theme.typography.fontFamily};
  background-color: ${props => props.$primary ? props.theme.colors.primary : props.theme.colors.surface};
  color: ${props => props.$primary ? props.theme.colors.textPrimary : props.theme.colors.primary};
  border: 1px solid ${props => props.$primary ? props.theme.colors.primary : props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius};
  padding: 0.55rem 1.1rem;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'default' : 'pointer'};
  opacity: ${props => props.disabled ? 0.6 : 1};
  white-space: nowrap;
`;
const FeedbackNote = styled.p`
  margin: 0 0 1rem 0;
  padding: 0.65rem 0.9rem;
  border-radius: ${props => props.theme.borderRadius};
  font-size: 0.85rem;
  background-color: ${props => props.$isError ? '#FEE2E2' : '#D1FAE5'};
  color: ${props => props.$isError ? '#991B1B' : '#065F46'};
`;
const ActividadGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(240px, 100%), 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;
const ActividadCard = styled.div`
  background-color: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius};
  box-shadow: ${props => props.theme.shadows.soft};
  border-left: 5px solid ${props => props.theme.colors.primary};
  padding: 1rem 1.25rem;
  h4 {
    margin: 0 0 0.35rem 0;
    font-size: 0.9rem;
    font-weight: 600;
    color: ${props => props.theme.colors.textSecondary};
  }
  strong {
    display: block;
    font-family: ${props => props.theme.typography.titleFamily};
    font-size: 1.6rem;
    font-variant-numeric: lining-nums tabular-nums;
    color: ${props => props.theme.colors.secondary};
    margin-bottom: 0.35rem;
  }
  p {
    margin: 0;
    font-size: 0.8rem;
    color: ${props => props.theme.colors.textSecondary};
  }
`;
const PendienteTag = styled.span`
  display: inline-block;
  margin-left: 0.35rem;
  padding: 0.05rem 0.45rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
  background-color: #FEF3C7;
  color: #92400E;
`;
const SOLID_GREEN = theme.colors.success.replace('0.15', '1');

const ACTIVIDAD_CARDS = [
    { key: 'operaciones', titulo: 'Operaciones del día' },
    { key: 'financiacion', titulo: 'Cauciones y pases del día' },
    { key: 'instrucciones', titulo: 'Instrucciones' },
];

const describirEstados = (grupo) => {
    const partes = [
        grupo.porEstado.Programada && `${grupo.porEstado.Programada} programada(s)`,
        grupo.porEstado.Pendiente && `${grupo.porEstado.Pendiente} pendiente(s)`,
        grupo.porEstado['En Proceso'] && `${grupo.porEstado['En Proceso']} en proceso`,
        grupo.porEstado.Hecha && `${grupo.porEstado.Hecha} hecha(s)`,
        grupo.porEstado.Rechazada && `${grupo.porEstado.Rechazada} rechazada(s)`,
    ].filter(Boolean);
    return partes.length > 0 ? partes.join(' · ') : 'Sin movimientos';
};

const renderActividadCliente = (actividad) => {
    if (!actividad) return <span style={{ color: theme.colors.textSecondary }}>—</span>;
    const total = actividad.operaciones.total + actividad.financiacion.total + actividad.instrucciones.total;
    const pendientes = pendientesDe(actividad.operaciones)
        + pendientesDe(actividad.financiacion)
        + pendientesDe(actividad.instrucciones);
    return (
        <span title={[
            `Operaciones: ${describirEstados(actividad.operaciones)}`,
            `Cauciones/pases: ${describirEstados(actividad.financiacion)}`,
            `Instrucciones: ${describirEstados(actividad.instrucciones)}`,
        ].join('\n')}>
            {total}
            {pendientes > 0 && <PendienteTag>{pendientes} sin terminar</PendienteTag>}
        </span>
    );
};

/**
 * "Mis Clientes" de un comercial o de un productor: saldos en pesos y dólares de
 * las cuentas de su cartera, calculados con la misma lógica que Control de
 * Saldos, la actividad del día (operaciones, cauciones/pases e instrucciones) y
 * las alertas de cuentas deudoras sin caución cargada. Click en un saldo abre el
 * desglose de movimientos del día.
 *
 * `cartera` es el doc del padrón del SGC (productores o comercialesClientes:
 * mismos campos `clientes`, `comitentes`, `clientesSinComitente`). Los totales se
 * acotan a esas cuentas, porque para un comercial el proveedor de saldos trae
 * toda la casa. `onRefrescar` (opcional) muestra el botón para traer del SGC un
 * cliente recién asignado.
 */
const MisClientesTab = ({ cartera, onRefrescar = null, onPrecargarCaucion, avisoSinCuentas }) => {
    const {
        saldosPorBucket,
        getSaldoComitente,
        clientesDeudoresSinPrecarga,
        snapshotActivo,
        tieneSnapshotInicialHoy,
        isLoadingSaldos,
    } = useSaldosClientes();

    const [search, setSearch] = useState('');
    const [desgloseModal, setDesgloseModal] = useState({ isOpen: false, cuenta: null, moneda: BUCKET_ARS });
    const [refresco, setRefresco] = useState({ running: false, message: '', isError: false });

    const comitentesKey = (cartera.comitentes || []).map(String).sort().join('|');
    const scope = useMemo(() => buildScope(comitentesKey ? comitentesKey.split('|') : []), [comitentesKey]);
    const actividad = useActividadClientes(cartera.comitentes || []);

    const handleRefrescar = async () => {
        setRefresco({ running: true, message: '', isError: false });
        try {
            const result = await onRefrescar();
            setRefresco({
                running: false,
                isError: false,
                message: result.sincronizado
                    ? `Padrón actualizado: ${result.comitentes} cuenta(s) asignada(s).`
                    : `Ya estaba actualizado hace instantes: ${result.comitentes} cuenta(s) asignada(s).`,
            });
        } catch (error) {
            console.error('No se pudo refrescar el padrón de clientes:', error);
            setRefresco({
                running: false,
                isError: true,
                message: 'No se pudo actualizar la lista de clientes. Intentá de nuevo en un momento.',
            });
        }
    };

    const rows = useMemo(() => {
        const term = search.trim().toLowerCase();
        return (cartera.clientes || [])
            .filter(c => !term
                || c.nombre?.toLowerCase().includes(term)
                || String(c.nroComitente).includes(term))
            .map(c => ({
                nroComitente: c.nroComitente,
                nombre: c.nombre,
                saldos: MONEDA_BUCKETS.reduce((acc, bucket) => ({
                    ...acc,
                    [bucket]: getSaldoComitente(c.nroComitente, MONEDA_MOVIMIENTO[bucket]),
                }), {}),
                actividad: actividad.porComitente.get(String(c.nroComitente)) || null,
            }))
            .sort((a, b) => (a.nombre || '').localeCompare(b.nombre || '', 'es'));
    }, [cartera.clientes, search, getSaldoComitente, actividad.porComitente]);

    const totalesPorMoneda = useMemo(
        () => MONEDA_BUCKETS.reduce((acc, bucket) => ({
            ...acc,
            [bucket]: sumSaldosTotales(filterMapByComitentes(saldosPorBucket[bucket], scope)),
        }), {}),
        [saldosPorBucket, scope],
    );
    const deudoresPorMoneda = useMemo(
        () => MONEDA_BUCKETS.reduce((acc, bucket) => ({
            ...acc,
            [bucket]: filterByComitentes(clientesDeudoresSinPrecarga(MONEDA_MOVIMIENTO[bucket]), scope),
        }), {}),
        [clientesDeudoresSinPrecarga, scope],
    );

    const openDesglose = (moneda) => (cuenta) => {
        const entry = saldosPorBucket[moneda].get(cuenta.nroComitente);
        setDesgloseModal({
            isOpen: true,
            moneda,
            cuenta: entry || getSaldoComitente(cuenta.nroComitente, MONEDA_MOVIMIENTO[moneda]),
        });
    };

    const sinCuentas = !cartera.comitentes || cartera.comitentes.length === 0;

    const acciones = onRefrescar && (
        <HeaderActions>
            <ActionButton onClick={handleRefrescar} disabled={refresco.running} title="Buscar clientes que ya te asignaron en el sistema comercial">
                {refresco.running ? 'Actualizando...' : 'Actualizar mis clientes'}
            </ActionButton>
        </HeaderActions>
    );

    const modales = (
        <SaldoDesgloseModal
            isOpen={desgloseModal.isOpen}
            onClose={() => setDesgloseModal({ isOpen: false, cuenta: null, moneda: 'ARS' })}
            cuenta={desgloseModal.cuenta}
            moneda={desgloseModal.moneda}
            snapshotInfo={snapshotActivo}
        />
    );

    if (sinCuentas) {
        return (
            <TabWrapper>
                <Header>
                    <Title>Mis Clientes</Title>
                    {acciones}
                </Header>
                {refresco.message && <FeedbackNote $isError={refresco.isError}>{refresco.message}</FeedbackNote>}
                <Placeholder>
                    Todavía no tenés cuentas comitente asignadas.
                    <br />
                    {avisoSinCuentas}
                </Placeholder>
                {modales}
            </TabWrapper>
        );
    }

    return (
        <TabWrapper>
            <Header>
                <Title>Mis Clientes</Title>
                <HeaderActions>
                    {snapshotActivo ? (
                        <SnapshotBadge>
                            Saldos del custodio: <strong>{snapshotActivo.tipo}</strong> @ {formatSnapshotHora(snapshotActivo.uploadedAt)}
                        </SnapshotBadge>
                    ) : (
                        <SnapshotBadge>Sin saldos del custodio cargados hoy</SnapshotBadge>
                    )}
                    {acciones}
                </HeaderActions>
            </Header>

            {refresco.message && <FeedbackNote $isError={refresco.isError}>{refresco.message}</FeedbackNote>}

            {cartera.clientesSinComitente?.length > 0 && (
                <InfoNote>
                    {cartera.clientesSinComitente.length === 1
                        ? '1 cliente asignado en el SGC no tiene número de comitente asociado y no se muestra: '
                        : `${cartera.clientesSinComitente.length} clientes asignados en el SGC no tienen número de comitente asociado y no se muestran: `}
                    {cartera.clientesSinComitente.join(', ')}. Avisá a Axon Capital para corregirlo.
                </InfoNote>
            )}

            <SectionLabel>Actividad del día</SectionLabel>
            <ActividadGrid>
                {ACTIVIDAD_CARDS.map(({ key, titulo }) => (
                    <ActividadCard key={key}>
                        <h4>{titulo}</h4>
                        <strong>{actividad.isLoading ? '…' : actividad.totales[key].total}</strong>
                        <p>{actividad.isLoading ? 'Cargando...' : describirEstados(actividad.totales[key])}</p>
                    </ActividadCard>
                ))}
            </ActividadGrid>

            {isLoadingSaldos ? (
                <Placeholder>Cargando saldos...</Placeholder>
            ) : (
                <>
                    {!tieneSnapshotInicialHoy && (
                        <WarningBanner>
                            Aún no se cargó el snapshot del custodio para hoy: los saldos se completan cuando Axon Capital lo suba.
                        </WarningBanner>
                    )}

                    {MONEDA_BUCKETS.some(b => deudoresPorMoneda[b].length > 0) && (
                        <AlertsGrid>
                            {MONEDA_BUCKETS
                                .filter(bucket => deudoresPorMoneda[bucket].length > 0)
                                .map(bucket => (
                                    <DeudoresAlertCard
                                        key={bucket}
                                        moneda={bucket}
                                        deudores={deudoresPorMoneda[bucket]}
                                        onOpenDesglose={openDesglose(bucket)}
                                        onPrecargarCaucion={onPrecargarCaucion}
                                    />
                                ))}
                        </AlertsGrid>
                    )}

                    <SectionLabel>Totales de mis cuentas (saldo real)</SectionLabel>
                    <IndicatorsGrid className="axon-grid-2">
                        {MONEDA_BUCKETS.map(bucket => (
                            <React.Fragment key={bucket}>
                                <IndicatorCard title={`Acreedores en ${MONEDA_TITULO[bucket]}`} value={totalesPorMoneda[bucket].totalAcreedor} currency={bucket} color={SOLID_GREEN} valueSize="1.5rem" compactMobile />
                                <IndicatorCard title={`Deudores en ${MONEDA_TITULO[bucket]}`} value={totalesPorMoneda[bucket].totalDeudor} currency={bucket} color={theme.colors.errorText} valueSize="1.5rem" compactMobile />
                            </React.Fragment>
                        ))}
                    </IndicatorsGrid>

                    <SearchInput
                        type="text"
                        placeholder="Buscar por nombre o comitente..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />

                    <TableContainer>
                        <StyledTable as={StackedTable} stack={CLIENTES_STACK}>
                            <thead>
                                <tr>
                                    <th>Nro Comitente</th>
                                    <th>Cliente</th>
                                    {MONEDA_BUCKETS.map(bucket => (
                                        <th
                                            key={bucket}
                                            style={{ textAlign: 'right' }}
                                            title={`Saldo proyectado en ${MONEDA_TITULO[bucket]} (y real, si difiere). Click para ver el desglose.`}
                                        >
                                            Saldo {MONEDA_TITULO[bucket]}
                                        </th>
                                    ))}
                                    <th
                                        style={{ textAlign: 'right' }}
                                        title="Operaciones, cauciones/pases e instrucciones de hoy. Pasá el mouse para ver el detalle por estado."
                                    >
                                        Actividad del día
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map(row => (
                                    <tr key={row.nroComitente}>
                                        <td>{formatComitenteForDisplay(row.nroComitente)}</td>
                                        <td>{row.nombre}</td>
                                        {MONEDA_BUCKETS.map(bucket => (
                                            <ClickableCell
                                                key={bucket}
                                                onClick={() => openDesglose(bucket)(row)}
                                                title={`Ver desglose en ${MONEDA_TITULO[bucket]}`}
                                            >
                                                <SaldoClienteBadge
                                                    saldoReal={row.saldos[bucket].saldoReal}
                                                    saldoProyectado={row.saldos[bucket].saldoProyectado}
                                                    moneda={MONEDA_MOVIMIENTO[bucket]}
                                                    tieneSnapshot={row.saldos[bucket].tieneSnapshot}
                                                />
                                            </ClickableCell>
                                        ))}
                                        <td style={{ textAlign: 'right' }}>{renderActividadCliente(row.actividad)}</td>
                                    </tr>
                                ))}
                                {rows.length === 0 && (
                                    <tr>
                                        <td colSpan={3 + MONEDA_BUCKETS.length} style={{ textAlign: 'center', fontStyle: 'italic', color: theme.colors.textSecondary }}>
                                            Ningún cliente coincide con la búsqueda.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </StyledTable>
                    </TableContainer>
                </>
            )}

            {modales}
        </TabWrapper>
    );
};

export default MisClientesTab;
